/**
 * Runtime Configuration Service (Azure Static Web Apps ARM Deployment Compatible)
 *
 * DESIGN PRINCIPLES:
 * 1. Fetch configuration from backend at runtime (not build time)
 * 2. Load config from /config endpoint before app initialization
 * 3. Cache configuration in memory after first load
 * 4. Provide same interface as environment.ts for compatibility
 *
 * This allows deploying the same build to multiple environments with different
 * configuration values (dev, staging, production) without rebuilding the frontend.
 */

export interface RuntimeConfig {
  // Azure AD Configuration
  azureClientId: string;
  azureTenantId: string;
  redirectUri: string;
  apiScope: string;

  // API Configuration
  apiUrl: string;

  // Domain Feed Configuration
  domainFeedUrl: string;
  domainFeedApiKey?: string;

  // Environment
  environment: string;
  appVersion?: string;

  // Datadog RUM (optional; RUM stays off unless both IDs are present)
  datadogRumApplicationId?: string;
  datadogRumClientToken?: string;
  datadogSite?: string;
  datadogEnv?: string;
}

export interface EnvironmentConfig {
  name: string;
  apiUrl: string;
  azureClientId: string;
  azureTenantId: string;
  redirectUri: string;
  domainFeedUrl: string;
  domainFeedApiKey?: string;
  msalConfig: {
    auth: {
      clientId: string;
      authority: string;
      redirectUri: string;
      postLogoutRedirectUri: string;
    };
    cache: {
      cacheLocation: 'localStorage';
      storeAuthStateInCookie: boolean;
    };
  };
  apiScopes: string[];
  graphScopes: string[];
}

class RuntimeConfigService {
  private config: RuntimeConfig | null = null;
  private loading: Promise<RuntimeConfig> | null = null;

  /**
   * Fetch configuration from backend /config endpoint
   */
  async loadConfig(): Promise<RuntimeConfig> {
    // If already loading, return the existing promise
    if (this.loading) {
      return this.loading;
    }

    // If already loaded, return cached config
    if (this.config) {
      return this.config;
    }

    // Start loading
    this.loading = this.fetchConfig();

    try {
      this.config = await this.loading;
      return this.config;
    } finally {
      this.loading = null;
    }
  }

  /**
   * Fetch configuration from backend
   */
  private async fetchConfig(): Promise<RuntimeConfig> {
    try {
      let config;

      if (process.env.REACT_APP_ENV_MODE === 'BUILTIN') {
        console.log('🔧 Using built-in environment variables for configuration');
        config = {
          azureClientId: process.env.REACT_APP_AZURE_CLIENT_ID,
          azureTenantId: process.env.REACT_APP_AZURE_TENANT_ID,
          apiUrl: process.env.REACT_APP_API_URL,
          redirectUri: process.env.REACT_APP_REDIRECT_URI,
          apiScope: process.env.REACT_APP_API_SCOPE || '',
          domainFeedUrl: process.env.REACT_APP_DOMAIN_FEED_URL || '',
          // domainFeedApiKey: process.env.REACT_APP_DOMAIN_FEED_API_KEY || '',
          environment: process.env.REACT_APP_ENVIRONMENT || 'production',
        };
      } else {      
        // Determine the backend URL based on current origin
        const backendUrl = this.determineBackendUrl();

        console.log(`🔧 Fetching runtime configuration from ${backendUrl}/api/config...`);

        const response = await fetch(`${backendUrl}/api/config`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch configuration: ${response.status} ${response.statusText}`);
        }

        const responseData = await response.json();

        // Extract config from {success: true, data: {...}} response structure
        config = responseData.success && responseData.data ? responseData.data : responseData;
      }

      // Validate required fields
      if (!config.azureClientId || !config.azureTenantId || !config.apiUrl || !config.redirectUri) {
        throw new Error(
          'Invalid configuration: Missing required fields (azureClientId, azureTenantId, apiUrl, or redirectUri)'
        );
      }

      console.log('✅ Runtime configuration loaded successfully', {
        environment: config.environment,
        apiUrl: config.apiUrl,
        redirectUri: config.redirectUri,
      });

      return config;
    } catch (error) {
      console.error('❌ Failed to load runtime configuration:', error);
      throw error;
    }
  }

  /**
   * Determine backend URL based on current window location
   */
  private determineBackendUrl(): string {
    // Production: use Static Web Apps /api (same domain, no CORS)
    // The /api/config endpoint will be served by Static Web Apps functions
    return window.location.origin;
  }

  /**
   * Get the current configuration (must be loaded first)
   */
  getConfig(): RuntimeConfig {
    if (!this.config) {
      throw new Error('Configuration not loaded. Call loadConfig() first.');
    }
    return this.config;
  }

  /**
   * Check if configuration is loaded
   */
  isLoaded(): boolean {
    return this.config !== null;
  }

  /**
   * Convert runtime config to EnvironmentConfig format for compatibility
   */
  toEnvironmentConfig(): EnvironmentConfig {
    const config = this.getConfig();

    return {
      name: config.environment || 'development',
      apiUrl: config.apiUrl,
      azureClientId: config.azureClientId,
      azureTenantId: config.azureTenantId,
      redirectUri: config.redirectUri,
      domainFeedUrl: config.domainFeedUrl,
      domainFeedApiKey: config.domainFeedApiKey,
      msalConfig: {
        auth: {
          clientId: config.azureClientId,
          authority: `https://login.microsoftonline.com/${config.azureTenantId}`,
          redirectUri: config.redirectUri,
          postLogoutRedirectUri: config.redirectUri,
        },
        cache: {
          cacheLocation: 'localStorage' as const,
          storeAuthStateInCookie: false,
        },
      },
      apiScopes: [config.apiScope],
      graphScopes: ['User.Read'],
    };
  }
}

// Export singleton instance
export const runtimeConfigService = new RuntimeConfigService();

// Export a function to get the config (throws if not loaded)
export function getRuntimeConfig(): EnvironmentConfig {
  return runtimeConfigService.toEnvironmentConfig();
}

// Export a function to check if config is loaded
export function isConfigLoaded(): boolean {
  return runtimeConfigService.isLoaded();
}

// Export the service for advanced usage
export default runtimeConfigService;
